import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api, { fmtINR, fmtDT } from "@/lib/api";
import { useBranding } from "@/context/BrandingContext";
import PoweredByFooter from "@/components/PoweredByFooter";

export default function DischargePrint() {
  const { ipdId } = useParams();
  const [d, setD] = useState(null);
  const [ipd, setIpd] = useState(null);
  const brand = useBranding();
  useEffect(() => {
    api.get(`/discharge/${ipdId}`).then((r) => setD(r.data));
    api.get(`/ipd/${ipdId}`).then((r) => setIpd(r.data));
  }, [ipdId]);
  useEffect(() => {
    if (d && ipd) {
      window.onafterprint = () => window.close();
      setTimeout(() => window.print(), 500);
    }
  }, [d, ipd]);

  useEffect(() => {
    document.documentElement.classList.add("print-mode-a4");
    return () => document.documentElement.classList.remove("print-mode-a4");
  }, []);
  if (!d || !ipd) return <div className="p-8">Loading...</div>;
  return (
    <div className="min-h-screen bg-slate-100 flex justify-center py-6">
      <div className="print-a4 shadow-lg" style={{fontSize:"12px"}}>
        <div className="flex items-center gap-3 border-b-2 border-red-700 pb-2">
          <img src={brand.logo_url} alt={brand.name} className="h-16 object-contain" />
          <div className="flex-1 text-center">
            <div className="font-display font-semibold text-lg leading-tight">{brand.name}</div>
            <div className="text-[10px] tracking-widest text-slate-600 uppercase">{brand.tagline}{brand.address ? ` · ${brand.address}` : ""}</div>
            {(brand.phones && brand.phones.length > 0) && (
              <div className="text-[10px] text-slate-500">{brand.phones.join(" · ")}</div>
            )}
          </div>
          <div className="text-right">
            <div className="font-bold">DISCHARGE SUMMARY</div>
            <div className="text-[10px]">Ref: {ipd.ipd_no}</div>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-2 mt-2 border-b pb-2 text-[11px]">
          <div><b>Name:</b> {d.patient_name}</div>
          <div><b>UHID:</b> <span className="font-mono">{d.uhid}</span></div>
          <div><b>Bed:</b> {ipd.bed_no}</div>
          <div><b>Admission:</b> {fmtDT(d.admission_date)}</div>
          <div><b>Discharge:</b> {fmtDT(d.discharge_date)}</div>
          <div><b>Doctor:</b> {d.doctor_name}</div>
        </div>
        <div className="mt-2 space-y-2">
          <div><b>DIAGNOSIS:</b> <span className="uppercase">{d.diagnosis}</span></div>
          <div><b>CONDITION AT DISCHARGE:</b> {d.condition}</div>
          {d.summary_text && (
            <div>
              <div className="font-bold mb-1">COURSE IN HOSPITAL</div>
              <div className="whitespace-pre-wrap text-[11.5px] leading-relaxed">{d.summary_text}</div>
            </div>
          )}
          {d.nursing_notes && (
            <div>
              <div className="font-bold mb-1">NURSING NOTES</div>
              <div className="whitespace-pre-wrap text-[11.5px] leading-relaxed">{d.nursing_notes}</div>
            </div>
          )}
          <div><b>ADVICE:</b> {d.advice}</div>
          <div><b>FOLLOW-UP:</b> {d.follow_up}</div>
        </div>
        <div className="mt-3">
          <div className="font-bold mb-1">FINAL BILL</div>
          <table className="w-full text-[11px]">
            <thead><tr className="border-b"><th className="text-left">Category</th><th className="text-left">Description</th><th className="text-right">Amount</th></tr></thead>
            <tbody>
              {ipd.charges.map((c) => <tr key={c.id} className="border-b border-dashed"><td>{c.category}</td><td>{c.description}</td><td className="text-right font-mono">{fmtINR(c.amount)}</td></tr>)}
              <tr><td colSpan="2" className="text-right font-bold pt-2">Total Billed</td><td className="text-right font-mono font-bold pt-2">{fmtINR(d.totals.billed)}</td></tr>
              <tr><td colSpan="2" className="text-right">Advance</td><td className="text-right font-mono">{fmtINR(d.totals.advance)}</td></tr>
              <tr><td colSpan="2" className="text-right">Paid</td><td className="text-right font-mono">{fmtINR(d.totals.paid)}</td></tr>
              <tr className="border-t"><td colSpan="2" className="text-right font-bold pt-1">Balance</td><td className="text-right font-mono font-bold pt-1">{fmtINR(d.totals.balance)}</td></tr>
            </tbody>
          </table>
        </div>
        <div className="mt-6 flex justify-end">
          <div className="text-center flex flex-col items-center">
            {brand.show_doctor_sig_rx && ipd?.doctor_id && (
              <img
                src={`${process.env.REACT_APP_BACKEND_URL}/api/doctors/${ipd.doctor_id}/signature`}
                alt=""
                style={{ maxHeight: 50, maxWidth: 180, objectFit: "contain", marginBottom: 2 }}
                onError={(e) => { e.target.style.display = "none"; }}
              />
            )}
            <div className="border-t border-slate-400 pt-1 w-48">
              <div>{d.doctor_name}</div><div className="text-[10px] text-slate-500">Consulting Doctor</div>
            </div>
          </div>
        </div>
        <PoweredByFooter />
      </div>
      <div className="fixed top-2 right-2 no-print">
        <button onClick={() => window.print()} className="bg-blue-700 text-white px-3 py-1 text-sm rounded-sm">Print</button>
      </div>
    </div>
  );
}
